import type { Tour } from "./types";

/**
 * Bridge tours — one per primary page, walking the migration funnel:
 * Parse (Cognos report XML) → Translate (target dialect) → Validate
 * (round-trip diff). History keeps every prior run; Settings exposes
 * provider + license state.
 */

const NAV = (path: string) => `nav[data-tour-id="nav"] a[href="${path}"]`;

export const parseTour: Tour = {
  id: "bridge.parse.v1",
  title: "Welcome to Bridge",
  subtitle: "Cognos → Power BI, Tableau, Qlik with confidence.",
  steps: [
    {
      placement: "center",
      title: "Migrations without losing nuance",
      body:
        "Bridge converts a Cognos report into a Power BI / Tableau / Qlik artifact in three reviewable stages — Parse, Translate, Validate. Every transformation is auditable; every diff is reversible.",
    },
    {
      target: '[data-tour-id="parse-upload"]',
      placement: "right",
      title: "Step 1 — Parse",
      body:
        "Drop a Cognos report XML (or paste it). Bridge lifts the data items and structure into a platform-agnostic IR — a tree representation that's the foundation for every translation that follows.",
    },
    {
      target: '[data-tour-id="parse-preview"]',
      placement: "left",
      title: "See the IR",
      body:
        "The right pane shows what Bridge extracted: every measure, dimension, and calculated field, with its lifted type and source. Anything that didn't fit the IR cleanly is flagged with the original Cognos formula preserved.",
    },
    {
      target: NAV("/translate"),
      placement: "bottom",
      title: "Next — Translate",
      body:
        "Once Parse is happy with the IR, move to Translate to pick a target platform and render. The replay button on the right always brings you back to a known good IR snapshot.",
    },
  ],
};

export const translateTour: Tour = {
  id: "bridge.translate.v1",
  title: "Translate",
  subtitle: "Pick a target dialect; Bridge renders.",
  steps: [
    {
      placement: "center",
      title: "Step 2 — Translate",
      body:
        "Bridge takes the IR and renders it in the target's native dialect — DAX for Power BI, Tableau formula for Tableau, qlikview-script for Qlik. Round-trip fidelity is unit-tested on every translator.",
    },
    {
      target: '[data-tour-id="translate-target"]',
      placement: "bottom",
      title: "Pick a target",
      body:
        "Switch targets without re-parsing. Each target shows a Confidence score: high means a clean structural mapping; lower means some IR concepts had to fall back to verbatim source formulas.",
    },
    {
      target: '[data-tour-id="translate-output"]',
      placement: "top",
      title: "Generated artifact",
      body:
        "The output pane shows the rendered artifact — copy it to your target environment, or push it via Bridge's deployment endpoint when configured. Side-by-side diff with source is one click away.",
    },
    {
      target: NAV("/validate"),
      placement: "bottom",
      title: "Next — Validate",
      body:
        "Move to Validate to confirm the rendered artifact reproduces the source's results. Bridge runs the IR through both source and target dialects and diffs row-by-row.",
    },
  ],
};

export const validateTour: Tour = {
  id: "bridge.validate.v1",
  title: "Validate",
  subtitle: "Round-trip diff against the source.",
  steps: [
    {
      placement: "center",
      title: "Step 3 — Validate",
      body:
        "The riskiest part of any migration is silent semantic drift — \"the new dashboard looks the same but the numbers are off by 0.3%\". Bridge catches this by re-running the rendered artifact and diffing against the source.",
    },
    {
      target: '[data-tour-id="validate-summary"]',
      placement: "bottom",
      title: "Pass / fail at a glance",
      body:
        "Pass means every measure round-trips byte-faithful or within tolerance. Fail itemizes which measures diverged, by how much, and where (cell-level granularity for tabular outputs).",
    },
    {
      target: '[data-tour-id="validate-details"]',
      placement: "top",
      title: "Drill into divergence",
      body:
        "For any failing row, Bridge shows the source value, target value, and the IR node that generated each. Most divergences trace to a single un-translated dialect quirk — fixable in one IR override.",
    },
  ],
};

export const historyTour: Tour = {
  id: "bridge.history.v1",
  title: "History",
  subtitle: "Every Parse → Translate → Validate run, replayable.",
  steps: [
    {
      placement: "center",
      title: "Audit + replay",
      body:
        "Every Bridge run is preserved with its source XML, IR snapshot, target output, and validation result. Replay any run to re-verify, or use it as the seed for a new translation against a different target.",
    },
    {
      target: '[data-tour-id="history-list"]',
      placement: "right",
      title: "Filter, replay, export",
      body:
        "Filter by source name, target platform, validation status, or date. Click any run to land in the Parse / Translate / Validate stages with that run's state restored.",
    },
  ],
};

export const settingsTour: Tour = {
  id: "bridge.settings.v1",
  title: "Settings",
  subtitle: "Provider, license, feature flags.",
  steps: [
    {
      placement: "center",
      title: "What Bridge believes about itself",
      body:
        "Bridge is mostly stateless — Parse + Translate + Validate work without external services. The LLM-explanation feature is the one optional enrichment; the read-only Settings view shows you what's currently active.",
    },
    {
      target: '[data-tour-id="settings-llm"]',
      placement: "right",
      title: "LLM provider (optional)",
      body:
        "When configured, Bridge uses the LLM to render plain-English explanations of why a translation diverged. When disabled, Bridge falls back to a deterministic explanation derived from the IR diff.",
    },
  ],
};

export function tourForPath(pathname: string): Tour | undefined {
  if (pathname.startsWith("/parse")) return parseTour;
  if (pathname.startsWith("/translate")) return translateTour;
  if (pathname.startsWith("/validate")) return validateTour;
  if (pathname.startsWith("/history")) return historyTour;
  if (pathname.startsWith("/settings")) return settingsTour;
  return undefined;
}
